/* eslint-disable prettier/prettier */
/* eslint-disable import/order */
import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button, Stack, TextField } from '@mui/material';
import { Form } from '@bvi/common-components';
import { useUpdateOrderMutation, useGetByIdOrderQuery } from '@bvi/admin-panel/entities/order/api-slice';

interface IEditOrderFormProperties {
  id: number;
  onSuccess: () => void;
}

interface IEditOrderFormValues {
  name: string;
}

export const EditOrderForm: FC<IEditOrderFormProperties> = ({
  id,
  onSuccess,
}) => {
  const { t } = useTranslation('translation');
  const [updateOrder, { isLoading: isUpdatingOrder }] = useUpdateOrderMutation();
  const { data: orderData } = useGetByIdOrderQuery(id);

  const order = orderData?.success ? orderData.payload.data : null;

  const validationSchema = z.object({
    name: z.string().min(1, t('manageList.form.fields.name.label') as string),
  });

  const methods = useForm<IEditOrderFormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: order?.name ?? '',
    },
    values: order ? {
      name: order.name,
    } : undefined,
  });

  const onSubmit = async (data: IEditOrderFormValues) => {
    try {
      await updateOrder([id, { name: data.name }]).unwrap();
      onSuccess();
    } catch {
      // Error handling - mutation will show error state
    }
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <TextField
            {...methods.register('name')}
            label={t('manageList.form.fields.name.label')}
            placeholder={t('manageList.form.fields.name.placeholder')}
            error={Boolean(methods.formState.errors.name)}
            helperText={methods.formState.errors.name?.message}
            fullWidth
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onSuccess}
              disabled={isUpdatingOrder}
            >
              {t('manageList.form.actions.cancel.caption')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdatingOrder}
            >
              {t('manageList.form.actions.save.caption')}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </FormProvider>
  );
};

